import React, { useState, useEffect } from 'react';
import { 
  X, Globe, Eye, EyeOff, Plus, Trash2, Save, Loader2, Award, 
  CheckCircle2, Layers, BookOpen, HelpCircle, Sparkles, MapPin, 
  Briefcase, Calendar, ShieldCheck, HeartHandshake, Check, AlertCircle, FileText
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';


const ToggleSwitch = ({ checked, onChange, disabled = false, activeColor = 'bg-indigo-600' }) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50 ${
        checked ? activeColor : 'bg-slate-200'
      }`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

const DoctorWebsiteModal = ({ isOpen, onClose, doctor, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  // Master visibility on website
  const [showOnWebsite, setShowOnWebsite] = useState(true);

  // Dynamic Website Profile Fields
  const [websiteAbout, setWebsiteAbout] = useState('');
  const [availabilityBadge, setAvailabilityBadge] = useState('Available for Consultation');
  const [consultationType, setConsultationType] = useState('In-person / Tele-Consultation');
  const [qualificationsSummary, setQualificationsSummary] = useState('');
  const [specializationOverride, setSpecializationOverride] = useState('');
  const [experienceOverride, setExperienceOverride] = useState('');
  const [locationOverride, setLocationOverride] = useState('');

  // Collections
  const [specializationsList, setSpecializationsList] = useState([]);
  const [newSpecTag, setNewSpecTag] = useState('');
  const [educationList, setEducationList] = useState([]);
  const [expertiseList, setExpertiseList] = useState([]);
  const [newExpertiseItem, setNewExpertiseItem] = useState('');
  const [proceduresList, setProceduresList] = useState([]);
  const [faqsList, setFaqsList] = useState([]);

  // Custom Quick Facts Builder
  const [customQuickFactsList, setCustomQuickFactsList] = useState([]);
  const [newCustomFactLabel, setNewCustomFactLabel] = useState('');
  const [newCustomFactValue, setNewCustomFactValue] = useState('');

  // Hero Section Visibility Toggles
  const [showVerifiedBadge, setShowVerifiedBadge] = useState(true);
  const [showAvailabilityBadge, setShowAvailabilityBadge] = useState(true);
  const [showQualificationsSummary, setShowQualificationsSummary] = useState(true);
  const [showDesignation, setShowDesignation] = useState(true);
  const [showExperience, setShowExperience] = useState(true);
  const [showLocation, setShowLocation] = useState(true);
  const [showBookAppointment, setShowBookAppointment] = useState(true);

  // Quick Facts Card Visibility Toggles
  const [showQuickFacts, setShowQuickFacts] = useState(true);
  const [showQuickFactExperience, setShowQuickFactExperience] = useState(true);
  const [showQuickFactSpecialization, setShowQuickFactSpecialization] = useState(true);
  const [showQuickFactConsultationType, setShowQuickFactConsultationType] = useState(true);
  const [showQuickFactLocation, setShowQuickFactLocation] = useState(true);

  // Section Visibility Toggles
  const [showAbout, setShowAbout] = useState(true);
  const [showSpecializations, setShowSpecializations] = useState(true);
  const [showEducation, setShowEducation] = useState(true);
  const [showAreasOfExpertise, setShowAreasOfExpertise] = useState(true);
  const [showProcedures, setShowProcedures] = useState(true);
  const [showFaqs, setShowFaqs] = useState(true);
  const [showRelatedDoctors, setShowRelatedDoctors] = useState(true);

  useEffect(() => {
    if (doctor) {
      const wp = doctor.websiteProfile || {};

      setShowOnWebsite(doctor.showOnWebsite !== false);

      const aboutText = Array.isArray(wp.about) && wp.about.length > 0
        ? wp.about.join('\n\n')
        : (wp.bio || doctor.bio || '');
      setWebsiteAbout(aboutText);

      setAvailabilityBadge(wp.availabilityBadge || 'Available for Consultation');
      setConsultationType(wp.consultationType || 'In-person / Tele-Consultation');
      setQualificationsSummary(wp.qualificationsSummary || doctor.primaryQualification || '');
      setSpecializationOverride(wp.specializationOverride || '');
      setExperienceOverride(wp.experienceOverride || '');
      setLocationOverride(wp.locationOverride || '');

      setSpecializationsList(Array.isArray(wp.specializations) && wp.specializations.length > 0 
        ? [...wp.specializations] 
        : (doctor.specialization ? [doctor.specialization] : []));

      setEducationList(Array.isArray(wp.education) && wp.education.length > 0 
        ? wp.education.map(e => ({ ...e }))
        : (doctor.primaryQualification ? [{ degree: doctor.primaryQualification, institution: doctor.universityName || '', year: doctor.yearOfCompletion ? String(doctor.yearOfCompletion) : '' }] : []));

      setExpertiseList(Array.isArray(wp.areasOfExpertise) && wp.areasOfExpertise.length > 0 
        ? [...wp.areasOfExpertise] 
        : (doctor.optionalExpertise ? doctor.optionalExpertise.split(',').map(s => s.trim()).filter(Boolean) : []));

      setProceduresList(Array.isArray(wp.procedures) && wp.procedures.length > 0
        ? wp.procedures.map(p => ({ ...p }))
        : []);

      setFaqsList(Array.isArray(wp.faqs) && wp.faqs.length > 0
        ? wp.faqs.map(f => ({ ...f }))
        : []);

      setCustomQuickFactsList(Array.isArray(wp.customQuickFacts) && wp.customQuickFacts.length > 0
        ? wp.customQuickFacts.map(q => ({ ...q }))
        : []);

      setShowVerifiedBadge(wp.showVerifiedBadge !== false);
      setShowAvailabilityBadge(wp.showAvailabilityBadge !== false);
      setShowQualificationsSummary(wp.showQualificationsSummary !== false);
      setShowDesignation(wp.showDesignation !== false);
      setShowExperience(wp.showExperience !== false);
      setShowLocation(wp.showLocation !== false);
      setShowBookAppointment(wp.showBookAppointment !== false);

      setShowQuickFacts(wp.showQuickFacts !== false);
      setShowQuickFactExperience(wp.showQuickFactExperience !== false);
      setShowQuickFactSpecialization(wp.showQuickFactSpecialization !== false);
      setShowQuickFactConsultationType(wp.showQuickFactConsultationType !== false);
      setShowQuickFactLocation(wp.showQuickFactLocation !== false);

      setShowAbout(wp.showAbout !== false);
      setShowSpecializations(wp.showSpecializations !== false);
      setShowEducation(wp.showEducation !== false);
      setShowAreasOfExpertise(wp.showAreasOfExpertise !== false);
      setShowProcedures(wp.showProcedures !== false);
      setShowFaqs(wp.showFaqs !== false);
      setShowRelatedDoctors(wp.showRelatedDoctors !== false);
    }
  }, [doctor, isOpen]);

  if (!isOpen || !doctor) return null;

  // Handlers for collections
  const handleAddSpecTag = (e) => {
    e?.preventDefault();
    if (newSpecTag.trim() && !specializationsList.includes(newSpecTag.trim())) {
      setSpecializationsList([...specializationsList, newSpecTag.trim()]);
      setNewSpecTag('');
    }
  };

  const handleRemoveSpecTag = (tag) => {
    setSpecializationsList(specializationsList.filter(t => t !== tag));
  };

  const handleAddEducation = () => {
    setEducationList([...educationList, { degree: '', institution: '', year: '' }]);
  };

  const handleUpdateEducation = (index, field, val) => {
    const updated = [...educationList];
    updated[index][field] = val;
    setEducationList(updated);
  };

  const handleRemoveEducation = (index) => {
    setEducationList(educationList.filter((_, i) => i !== index));
  };

  const handleAddExpertise = (e) => {
    e?.preventDefault();
    if (newExpertiseItem.trim() && !expertiseList.includes(newExpertiseItem.trim())) {
      setExpertiseList([...expertiseList, newExpertiseItem.trim()]);
      setNewExpertiseItem('');
    }
  };

  const handleRemoveExpertise = (item) => {
    setExpertiseList(expertiseList.filter(i => i !== item));
  };

  const handleAddProcedure = () => {
    setProceduresList([...proceduresList, { title: '', description: '' }]);
  };

  const handleUpdateProcedure = (index, field, val) => {
    const updated = [...proceduresList];
    updated[index][field] = val;
    setProceduresList(updated);
  };

  const handleRemoveProcedure = (index) => {
    setProceduresList(proceduresList.filter((_, i) => i !== index));
  };

  const handleAddFaq = () => {
    setFaqsList([...faqsList, { question: '', answer: '' }]);
  };

  const handleUpdateFaq = (index, field, val) => {
    const updated = [...faqsList];
    updated[index][field] = val;
    setFaqsList(updated);
  };

  const handleRemoveFaq = (index) => {
    setFaqsList(faqsList.filter((_, i) => i !== index));
  };

  const handleAddCustomFact = (e) => {
    e?.preventDefault();
    if (!newCustomFactLabel.trim() || !newCustomFactValue.trim()) {
      toast.error('Both Label and Value are required for a custom fact');
      return;
    }
    setCustomQuickFactsList([...customQuickFactsList, { label: newCustomFactLabel.trim(), value: newCustomFactValue.trim() }]);
    setNewCustomFactLabel('');
    setNewCustomFactValue('');
  };

  const handleUpdateCustomFact = (index, field, val) => {
    const updated = [...customQuickFactsList];
    updated[index][field] = val;
    setCustomQuickFactsList(updated);
  };

  const handleRemoveCustomFact = (index) => {
    setCustomQuickFactsList(customQuickFactsList.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const payload = {
        showOnWebsite,
        bio: websiteAbout.trim() || doctor.bio,
        websiteProfile: {
          about: websiteAbout.split('\n').map(p => p.trim()).filter(Boolean),
          bio: websiteAbout.trim(),
          availabilityBadge: availabilityBadge.trim() || 'Available for Consultation',
          consultationType: consultationType.trim() || 'In-person / Tele-Consultation',
          qualificationsSummary: qualificationsSummary.trim(),
          specializationOverride: specializationOverride.trim(),
          experienceOverride: experienceOverride.trim(),
          locationOverride: locationOverride.trim(),
          specializations: specializationsList.map(s => s.trim()).filter(Boolean),
          education: educationList.filter(e => e.degree?.trim() || e.institution?.trim() || e.year?.trim()),
          areasOfExpertise: expertiseList.map(e => e.trim()).filter(Boolean),
          procedures: proceduresList.filter(p => p.title?.trim()),
          faqs: faqsList.filter(f => f.question?.trim() && f.answer?.trim()),
          customQuickFacts: customQuickFactsList.filter(f => f.label?.trim() && f.value?.trim()),

          showVerifiedBadge,
          showAvailabilityBadge,
          showQualificationsSummary,
          showDesignation,
          showExperience,
          showLocation,
          showBookAppointment,

          showQuickFacts,
          showQuickFactExperience,
          showQuickFactSpecialization,
          showQuickFactConsultationType,
          showQuickFactLocation,

          showAbout,
          showSpecializations,
          showEducation,
          showAreasOfExpertise,
          showProcedures,
          showFaqs,
          showRelatedDoctors
        }
      };

      const response = await api.put(`/doctors/${doctor._id || doctor.id}`, payload);
      if (response.data.success) {
        toast.success('Website profile settings updated successfully!');
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update website profile');
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: 'hero', label: 'Hero Card & Header', icon: Sparkles },
    { id: 'quickfacts', label: 'Quick Facts & Custom Fields', icon: ShieldCheck },
    { id: 'content', label: 'Page Tabs & Content', icon: BookOpen },
    { id: 'faq', label: 'Procedures & FAQs', icon: HelpCircle }
  ];

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="bg-white w-full max-w-5xl h-[95vh] md:h-[90vh] overflow-hidden rounded-[32px] md:rounded-[40px] shadow-2xl flex flex-col relative animate-in fade-in zoom-in duration-300">
        
        {/* Header */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-b border-slate-100 flex items-center justify-between bg-white relative z-10 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner shrink-0">
              <Globe className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg md:text-xl font-black text-[#143E78] tracking-tight uppercase">
                  Website Profile Settings
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700">
                  doxez.in
                </span>
              </div>
              <p className="text-[10px] md:text-xs font-bold text-slate-400 mt-0.5">
                Customize doctor details page fields, visibility toggles, and custom quick facts for {doctor?.name || 'doctor'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-50 rounded-full transition-all text-slate-400 hover:text-slate-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Section switcher */}
        <div className="px-6 md:px-10 bg-slate-50 border-b border-slate-100 flex gap-2 overflow-x-auto py-2.5 shrink-0">
          {sections.map(sec => {
            const SecIcon = sec.icon;
            const isActive = activeSection === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => setActiveSection(sec.id)}
                className={`px-4 py-2 rounded-xl text-xs font-black tracking-wider uppercase transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100 scale-102'
                    : 'text-slate-500 hover:bg-white/80 hover:text-slate-800'
                }`}
              >
                <SecIcon className="w-4 h-4" />
                {sec.label}
              </button>
            );
          })}
        </div>

        {/* Body Form */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-10 md:py-8 space-y-6">

          {/* SECTION 1: HERO CARD & HEADER */}
          {activeSection === 'hero' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Info banner */}
              <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-2xl flex items-start gap-3">
                <Globe className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                <div className="text-xs text-indigo-950 leading-relaxed">
                  <span className="font-bold">Hero Card Field Controls:</span> Toggle switches below to instantly show or hide badges, experience stats, and action buttons on the top doctor hero card. Overrides allow displaying friendly custom titles without altering the internal registration data.
                </div>
              </div>

              {/* Master website visibility */}
              <div className="p-4 bg-white border border-slate-200 rounded-2xl flex items-center justify-between shadow-sm">
                <div>
                  <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Publish Doctor On Website</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">When disabled, this doctor's page returns 404 and is hidden from search/listings.</p>
                </div>
                <ToggleSwitch
                  checked={showOnWebsite}
                  onChange={setShowOnWebsite}
                  activeColor="bg-emerald-600"
                />
              </div>

              {/* Toggles Grid */}
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6">
                <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-indigo-600" />
                  Hero Element Visibility Toggles
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {[
                    { label: 'Verified Checkmark Badge', state: showVerifiedBadge, setter: setShowVerifiedBadge, desc: 'Blue checkmark icon beside doctor name' },
                    { label: 'Status Pill ("Available for Consultation")', state: showAvailabilityBadge, setter: setShowAvailabilityBadge, desc: 'Green consultation availability pill' },
                    { label: 'Qualifications Subtitle', state: showQualificationsSummary, setter: setShowQualificationsSummary, desc: 'e.g. MBBS, MS below doctor name' },
                    { label: 'Specialization Pill', state: showDesignation, setter: setShowDesignation, desc: 'e.g. Orthopaedics / General Surgery pill' },
                    { label: 'Experience Stat Box', state: showExperience, setter: setShowExperience, desc: 'Briefcase icon box (e.g. 5+ Years Experience)' },
                    { label: 'Location Stat Box', state: showLocation, setter: setShowLocation, desc: 'Map pin icon box (e.g. Bhubaneswar)' },
                    { label: '"Book Appointment" CTA Button', state: showBookAppointment, setter: setShowBookAppointment, desc: 'Primary blue booking button in hero header' }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-200/80 flex items-center justify-between shadow-xs hover:border-slate-300 transition-colors">
                      <div className="flex-1 pr-3">
                        <span className="text-xs font-black text-slate-800 block">{item.label}</span>
                        <span className="text-[11px] text-slate-400 block mt-0.5 leading-snug">{item.desc}</span>
                      </div>
                      <ToggleSwitch checked={item.state} onChange={item.setter} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Text Overrides */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-600" />
                  Hero Field Text Overrides (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                      Availability Badge Text
                    </label>
                    <input
                      type="text"
                      value={availabilityBadge}
                      onChange={(e) => setAvailabilityBadge(e.target.value)}
                      placeholder="Available for Consultation"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                      Qualifications Subtitle Text
                    </label>
                    <input
                      type="text"
                      value={qualificationsSummary}
                      onChange={(e) => setQualificationsSummary(e.target.value)}
                      placeholder="e.g. MBBS, MS, Fellowship in Arthroscopy"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                      Specialization Override
                    </label>
                    <input
                      type="text"
                      value={specializationOverride}
                      onChange={(e) => setSpecializationOverride(e.target.value)}
                      placeholder={doctor.specialization || 'e.g. Orthopaedics'}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                      Experience Display Override
                    </label>
                    <input
                      type="text"
                      value={experienceOverride}
                      onChange={(e) => setExperienceOverride(e.target.value)}
                      placeholder="e.g. 5+ Years or 12+"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                      Location Display Override
                    </label>
                    <input
                      type="text"
                      value={locationOverride}
                      onChange={(e) => setLocationOverride(e.target.value)}
                      placeholder="e.g. Bhubaneswar or Cuttack, Odisha"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 2: QUICK FACTS & CUSTOM FIELDS */}
          {activeSection === 'quickfacts' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Quick Facts Card Master Toggle */}
              <div className="p-4 bg-white border border-slate-200 rounded-2xl flex items-center justify-between shadow-sm">
                <div>
                  <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Quick Facts Sidebar Card</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">Controls whether the Quick Facts card beside the About section is displayed.</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-bold text-slate-600">{showQuickFacts ? 'Card Enabled' : 'Card Hidden'}</span>
                  <ToggleSwitch checked={showQuickFacts} onChange={setShowQuickFacts} />
                </div>
              </div>

              {/* Standard Fact Rows */}
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 space-y-4">
                <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider flex items-center gap-2">
                  <Eye className="w-4 h-4 text-indigo-600" />
                  Standard Quick Fact Rows
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { label: 'Experience Row', state: showQuickFactExperience, setter: setShowQuickFactExperience, desc: 'Displays Experience count' },
                    { label: 'Specialization Row', state: showQuickFactSpecialization, setter: setShowQuickFactSpecialization, desc: 'Displays Doctor Specialization' },
                    { label: 'Consultation Type Row', state: showQuickFactConsultationType, setter: setShowQuickFactConsultationType, desc: 'Displays Consultation Type' },
                    { label: 'Location Row', state: showQuickFactLocation, setter: setShowQuickFactLocation, desc: 'Displays Doctor Location' }
                  ].map((row, idx) => (
                    <div key={idx} className="bg-white p-3.5 rounded-2xl border border-slate-200/80 flex items-center justify-between hover:border-slate-300 transition-colors">
                      <div className="flex-1 pr-3">
                        <span className="text-xs font-black text-slate-800 block">{row.label}</span>
                        <span className="text-[11px] text-slate-400 block mt-0.5">{row.desc}</span>
                      </div>
                      <ToggleSwitch checked={row.state} onChange={row.setter} />
                    </div>
                  ))}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                    Consultation Type Text
                  </label>
                  <input
                    type="text"
                    value={consultationType}
                    onChange={(e) => setConsultationType(e.target.value)}
                    placeholder="In-person / Tele-Consultation"
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* DYNAMIC CUSTOM QUICK FACTS BUILDER */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-600" />
                      Dynamic Custom Quick Facts
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Add any extra facts (e.g. OPD Timings, Languages, Surgeries Done) to show in the Quick Facts card.
                    </p>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full">
                    {customQuickFactsList.length} custom fact{customQuickFactsList.length === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Add new fact input row */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col md:flex-row items-center gap-3">
                  <input
                    type="text"
                    placeholder="Field Label (e.g. Languages)"
                    value={newCustomFactLabel}
                    onChange={(e) => setNewCustomFactLabel(e.target.value)}
                    className="w-full md:w-1/3 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Value (e.g. English, Hindi, Odia)"
                    value={newCustomFactValue}
                    onChange={(e) => setNewCustomFactValue(e.target.value)}
                    className="w-full md:w-1/2 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomFact}
                    className="w-full md:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                    Add Fact
                  </button>
                </div>

                {/* List of existing custom facts */}
                <div className="space-y-2.5">
                  {customQuickFactsList.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-2xl text-xs text-slate-400 font-bold">
                      No custom quick facts added yet. Use the inputs above to add one.
                    </div>
                  ) : (
                    customQuickFactsList.map((fact, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200/90 rounded-2xl">
                        <input
                          type="text"
                          value={fact.label}
                          onChange={(e) => handleUpdateCustomFact(idx, 'label', e.target.value)}
                          className="w-1/3 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                          placeholder="Label"
                        />
                        <input
                          type="text"
                          value={fact.value}
                          onChange={(e) => handleUpdateCustomFact(idx, 'value', e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                          placeholder="Value"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomFact(idx)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Remove custom fact"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* SECTION 3: PAGE TABS & CONTENT */}
          {activeSection === 'content' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* About section */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">About Doctor Section</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Custom bio paragraphs displayed on the website Doctor Details page.</p>
                  </div>
                  <ToggleSwitch checked={showAbout} onChange={setShowAbout} />
                </div>
                {showAbout && (
                  <textarea
                    rows={4}
                    value={websiteAbout}
                    onChange={(e) => setWebsiteAbout(e.target.value)}
                    placeholder="Enter customized professional overview for this doctor. Separate paragraphs with a blank line."
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-medium text-slate-800 text-xs outline-none focus:border-indigo-500 leading-relaxed"
                  />
                )}
              </div>

              {/* Specializations Tag List */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Specializations Section</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Specialization badges displayed in the tabs and profile overview.</p>
                  </div>
                  <ToggleSwitch checked={showSpecializations} onChange={setShowSpecializations} />
                </div>
                {showSpecializations && (
                  <>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSpecTag}
                        onChange={(e) => setNewSpecTag(e.target.value)}
                        placeholder="Add specialization tag (e.g. Joint Replacement Surgery)"
                        className="flex-1 px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSpecTag(e)}
                      />
                      <button
                        type="button"
                        onClick={handleAddSpecTag}
                        className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase rounded-xl transition-all"
                      >
                        Add Tag
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {specializationsList.map((tag, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-xl border border-indigo-100 flex items-center gap-2">
                          {tag}
                          <button type="button" onClick={() => handleRemoveSpecTag(tag)} className="hover:text-red-600">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Education / Qualifications List */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Education & Qualifications</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Degrees and university certifications shown under Qualifications tab.</p>
                  </div>
                  <ToggleSwitch checked={showEducation} onChange={setShowEducation} />
                </div>

                {showEducation && (
                  <div className="space-y-3">
                    {educationList.map((edu, idx) => (
                      <div key={idx} className="flex flex-col md:flex-row gap-2.5 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl items-center">
                        <input
                          type="text"
                          value={edu.degree}
                          onChange={(e) => handleUpdateEducation(idx, 'degree', e.target.value)}
                          placeholder="Degree (e.g. MS - Orthopaedics)"
                          className="w-full md:w-1/3 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                        />
                        <input
                          type="text"
                          value={edu.institution}
                          onChange={(e) => handleUpdateEducation(idx, 'institution', e.target.value)}
                          placeholder="Institution (e.g. AIIMS New Delhi)"
                          className="w-full md:flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                        />
                        <input
                          type="text"
                          value={edu.year}
                          onChange={(e) => handleUpdateEducation(idx, 'year', e.target.value)}
                          placeholder="Year"
                          className="w-full md:w-24 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveEducation(idx)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddEducation}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black uppercase rounded-xl transition-all flex items-center gap-1.5"
                    >
                      <Plus className="w-4 h-4" /> Add Degree
                    </button>
                  </div>
                )}
              </div>

              {/* Areas of Expertise */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Areas of Expertise</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Checklist of clinical specialties and treatments.</p>
                  </div>
                  <ToggleSwitch checked={showAreasOfExpertise} onChange={setShowAreasOfExpertise} />
                </div>
                {showAreasOfExpertise && (
                  <>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newExpertiseItem}
                        onChange={(e) => setNewExpertiseItem(e.target.value)}
                        placeholder="Add expertise (e.g. Arthroscopic ACL Reconstruction)"
                        className="flex-1 px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-xs outline-none focus:border-indigo-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddExpertise(e)}
                      />
                      <button
                        type="button"
                        onClick={handleAddExpertise}
                        className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase rounded-xl transition-all"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {expertiseList.map((item, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-xl border border-emerald-100 flex items-center gap-2">
                          {item}
                          <button type="button" onClick={() => handleRemoveExpertise(item)} className="hover:text-red-600">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* SECTION 4: PROCEDURES & FAQS */}
          {activeSection === 'faq' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Procedures Section */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Procedures Tab</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Surgical and clinical procedures performed by this doctor.</p>
                  </div>
                  <ToggleSwitch checked={showProcedures} onChange={setShowProcedures} />
                </div>
                {showProcedures && (
                  <div className="space-y-3">
                    {proceduresList.map((proc, idx) => (
                      <div key={idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-slate-700">Procedure #{idx + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveProcedure(idx)}
                            className="p-1 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={proc.title}
                          onChange={(e) => handleUpdateProcedure(idx, 'title', e.target.value)}
                          placeholder="Procedure title (e.g. Total Hip Replacement)"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                        />
                        <textarea
                          rows={2}
                          value={proc.description}
                          onChange={(e) => handleUpdateProcedure(idx, 'description', e.target.value)}
                          placeholder="Brief clinical description or benefits"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddProcedure}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black uppercase rounded-xl transition-all flex items-center gap-1.5"
                    >
                      <Plus className="w-4 h-4" /> Add Procedure
                    </button>
                  </div>
                )}
              </div>

              {/* FAQs Section */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">Frequently Asked Questions (FAQs)</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Doctor-specific questions & answers displayed at the bottom of the page.</p>
                  </div>
                  <ToggleSwitch checked={showFaqs} onChange={setShowFaqs} />
                </div>
                {showFaqs && (
                  <div className="space-y-3">
                    {faqsList.map((faq, idx) => (
                      <div key={idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-slate-700">FAQ #{idx + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFaq(idx)}
                            className="p-1 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => handleUpdateFaq(idx, 'question', e.target.value)}
                          placeholder="Question"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
                        />
                        <textarea
                          rows={2}
                          value={faq.answer}
                          onChange={(e) => handleUpdateFaq(idx, 'answer', e.target.value)}
                          placeholder="Answer"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddFaq}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-black uppercase rounded-xl transition-all flex items-center gap-1.5"
                    >
                      <Plus className="w-4 h-4" /> Add FAQ
                    </button>
                  </div>
                )}
              </div>

              {/* Related Doctors Toggle */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-black text-[#143E78] uppercase tracking-wider">"You May Also Consider" Doctors Section</h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">Controls whether related specialist doctor cards appear at the bottom.</p>
                </div>
                <ToggleSwitch checked={showRelatedDoctors} onChange={setShowRelatedDoctors} />
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-5 md:px-10 md:py-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-[32px] md:rounded-b-[40px] shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-indigo-900 transition-all shadow-lg active:scale-95 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {loading ? 'Saving Changes...' : 'Save & Publish to Website'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default DoctorWebsiteModal;
